import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import { API, api, fileUrl } from "@/lib/api";
import ChatWidget from "@/components/ChatWidget";
import ImageCarousel from "@/components/ImageCarousel";
import BulletText from "@/components/BulletText";
import CustomerOnboarding from "@/components/CustomerOnboarding";
import { getCustomerSession, isLiked, toggleLikedLocally } from "@/lib/showroomSession";
import { ChevronLeft, Sparkles, Download, ChevronDown, Heart } from "lucide-react";

export default function PublicProduct() {
  const { code } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState("");
  const [needsSession, setNeedsSession] = useState(false);

  const fetchProduct = React.useCallback(() => {
    setErr("");
    api
      .get(`/public/products/${code}`)
      .then((r) => setP(r.data))
      .catch((e) => {
        if (e?.response?.status === 401) {
          setNeedsSession(true);
        } else {
          setErr(e?.response?.data?.detail || "Product not found");
        }
      });
  }, [code]);

  useEffect(() => {
    // Only fetch if a session (staff or customer) exists.
    const token = localStorage.getItem("heda_token");
    const session = getCustomerSession();
    if (!token && !session) {
      setNeedsSession(true);
      return;
    }
    fetchProduct();
  }, [code, fetchProduct]);

  // Re-fetch whenever a customer session is started elsewhere.
  useEffect(() => {
    const handler = () => {
      setNeedsSession(false);
      fetchProduct();
    };
    window.addEventListener("heda-customer-session-changed", handler);
    return () => window.removeEventListener("heda-customer-session-changed", handler);
  }, [fetchProduct]);

  // Like / Unlike handler — logs event AND updates local session state.
  const onToggleLike = async () => {
    if (!p) return;
    const currently = isLiked(p.id);
    const newType = currently ? "unlike" : "like";
    toggleLikedLocally(p.id, !currently);
    try {
      await api.post("/showroom/event", {
        type: newType,
        product_id: p.id,
        product_code: p.code,
        product_name: p.name,
      });
    } catch {
      /* network may be down; like state stays local */
    }
  };

  if (needsSession) {
    return (
      <PublicLayout>
        <div className="max-w-md mx-auto px-6 py-16 text-center">
          <Sparkles size={28} strokeWidth={1.5} className="mx-auto text-[#B5953B] mb-3" />
          <div className="hed-overline">Private catalog</div>
          <h1 className="font-serif text-3xl mt-3 tracking-tight">
            Please ask the showroom staff.
          </h1>
          <p className="text-sm text-[#5C5C54] mt-3">
            The Heda catalog is private. A staff member can start a session for you in seconds —
            they&rsquo;ll just need your name and mobile number so we can show you what suits you best.
          </p>
        </div>
        <CustomerOnboarding open={false} onClose={() => {}} />
      </PublicLayout>
    );
  }

  if (err) {
    return (
      <PublicLayout>
        <div className="max-w-2xl mx-auto px-6 py-24 text-center">
          <div className="hed-overline">Not found</div>
          <h1 className="font-serif text-4xl mt-4">This product page isn&rsquo;t available.</h1>
          <p className="text-sm text-[#5C5C54] mt-3">{err}</p>
          <Link
            to="/search"
            className="inline-flex items-center gap-2 mt-8 bg-[#1A1A18] text-white px-5 py-3 rounded-sm text-sm hover:bg-[#2a2a26]"
          >
            <ChevronLeft size={14} strokeWidth={2} /> Browse products
          </Link>
        </div>
      </PublicLayout>
    );
  }

  if (!p) {
    return (
      <PublicLayout>
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-16 text-sm text-[#5C5C54]">
          Loading...
        </div>
      </PublicLayout>
    );
  }

  const images = p.images || [];
  const approved = p.approved_suggestions || {};

  // Smart size/dimensions consolidation:
  // If we have available_sizes, that IS the dimensions info — show one combined "Sizes" line.
  const primarySize =
    p.available_sizes?.[0] || p.dimensions || null;
  const extraSizes = p.available_sizes && p.available_sizes.length > 1
    ? p.available_sizes.slice(1).join(", ")
    : null;

  return (
    <PublicLayout>
      <article className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-5 md:py-12 w-full min-w-0">
        {/* Hero grid */}
        <div className="grid lg:grid-cols-2 gap-6 md:gap-10 lg:gap-16">
          {/* Gallery */}
          <div className="hed-fade-up">
            <ImageCarousel images={images} productName={p.name} />
          </div>

          {/* Title + key info */}
          <div className="hed-fade-up">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              {p.category && <span className="hed-overline">{p.category}</span>}
              {p.material && (
                <>
                  <span className="text-[#E8E6DF]">·</span>
                  <span className="hed-overline" style={{ color: "#5C5C54" }}>
                    {p.material}
                  </span>
                </>
              )}
            </div>
            <h1 className="font-display text-[34px] sm:text-5xl md:text-6xl tracking-tight leading-[1.05] break-words">
              {p.name}
            </h1>
            <div className="mt-2 flex items-center gap-3 flex-wrap">
              <div className="text-[11px] text-[#5C5C54] font-mono tracking-wider">
                CODE · {p.code}
              </div>
              {getCustomerSession() && (
                <button
                  onClick={onToggleLike}
                  data-testid="public-like-button"
                  className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-full border transition-colors ${
                    isLiked(p.id)
                      ? "bg-[#9E2A2B] text-white border-[#9E2A2B] hover:bg-[#7d2122]"
                      : "bg-white text-[#5C5C54] border-[#E8E6DF] hover:border-[#9E2A2B] hover:text-[#9E2A2B]"
                  }`}
                >
                  <Heart
                    size={12}
                    strokeWidth={2}
                    className={isLiked(p.id) ? "fill-white" : ""}
                  />
                  {isLiked(p.id) ? "Liked" : "Like"}
                </button>
              )}
            </div>

            {p.description && (
              <p className="mt-5 text-[15px] text-[#1A1A18]/85 leading-relaxed max-w-xl">
                {p.description}
              </p>
            )}

            {p.applications?.length > 0 && (
              <div className="mt-6 flex flex-wrap gap-2">
                {p.applications.slice(0, 6).map((a, i) => (
                  <span
                    key={i}
                    className="hed-chip text-[12px]"
                    data-testid={`application-chip-${i}`}
                  >
                    {a.length > 40 ? a.slice(0, 38) + "…" : a}
                  </span>
                ))}
              </div>
            )}

            {/* Pricing block */}
            {p.prices?.length > 0 && (
              <div className="mt-6 border border-[#E8E6DF] bg-[#F2F0EA] rounded-sm p-4">
                <div className="hed-overline mb-2">Price</div>
                <div className="space-y-1.5">
                  {p.prices.map((pr, i) => (
                    <div
                      key={i}
                      className="flex items-baseline justify-between gap-3"
                      data-testid={`price-display-${i}`}
                    >
                      <div className="flex items-baseline gap-1.5">
                        <span className="text-[#5C5C54] text-[13px]">
                          {pr.currency || "₹"}
                        </span>
                        <span className="font-serif text-2xl text-[#1A1A18]">
                          {Number(pr.value).toLocaleString("en-IN", {
                            maximumFractionDigits: 2,
                          })}
                        </span>
                        <span className="text-[12px] text-[#5C5C54]">
                          / {unitLabel(pr.unit)}
                        </span>
                      </div>
                      {pr.notes && (
                        <span className="text-[11px] text-[#5C5C54] italic">{pr.notes}</span>
                      )}
                    </div>
                  ))}
                </div>
                <div className="text-[11px] text-[#5C5C54] mt-2">
                  Prices indicative · contact us for bulk and trade pricing.
                </div>
              </div>
            )}

            {/* Key info strip — only show fields with values */}
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-4 border-t border-[#E8E6DF] pt-5">
              {p.thickness && <KV label="Thickness" value={p.thickness} />}
              {primarySize && (
                <KV
                  label="Available Size"
                  value={
                    extraSizes ? (
                      <>
                        {primarySize}
                        <div className="text-[10px] text-[#5C5C54] font-medium mt-0.5">
                          +{extraSizes}
                        </div>
                      </>
                    ) : (
                      primarySize
                    )
                  }
                />
              )}
              {p.material && <KV label="Material" value={p.material} />}
            </div>

            <div className="mt-7">
              <button
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#B5953B] hover:bg-[#9A7E32] text-white px-6 py-3 rounded-sm text-sm font-semibold transition-colors"
                onClick={() => {
                  document
                    .getElementById("specifications")
                    ?.scrollIntoView({ behavior: "smooth" });
                }}
                data-testid="view-specs-button"
              >
                View full specifications
                <ChevronDown size={14} strokeWidth={2} />
              </button>
            </div>
          </div>
        </div>

        {/* Long-form sections */}
        <div
          className="mt-12 md:mt-20 grid lg:grid-cols-3 gap-8 md:gap-10 lg:gap-16"
          id="specifications"
        >
          <div className="lg:col-span-2 space-y-10 md:space-y-12">
            {p.specifications?.length > 0 && (
              <section>
                <div className="hed-overline">Specifications</div>
                <h2 className="font-serif text-3xl md:text-4xl mt-2 mb-4">
                  The exact numbers.
                </h2>
                <div className="border-t border-[#E8E6DF]">
                  {p.specifications.map((s, idx) => (
                    <div
                      key={idx}
                      className="grid grid-cols-3 gap-4 py-3.5 border-b border-[#E8E6DF] text-sm"
                    >
                      <div className="text-[#5C5C54]">{s.key}</div>
                      <div className="col-span-2 text-[#1A1A18] font-semibold">
                        {s.value}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {p.variants?.length > 0 && (
              <section data-testid="public-variants-section">
                <div className="hed-overline">Profile Variants</div>
                <h2 className="font-serif text-3xl md:text-4xl mt-2 mb-4">
                  Choose your profile.
                </h2>
                <div className="overflow-x-auto -mx-5 md:mx-0 px-5 md:px-0">
                  <table className="w-full min-w-[520px] text-sm border-t border-[#E8E6DF]">
                    <thead>
                      <tr className="text-left text-[11px] text-[#5C5C54] uppercase tracking-wider">
                        <th className="py-3 pr-4 font-medium">Code</th>
                        <th className="py-3 pr-4 font-medium">Finish</th>
                        <th className="py-3 pr-4 font-medium">Length</th>
                        <th className="py-3 pr-4 font-medium">Coverage</th>
                        <th className="py-3 pr-4 font-medium">Weight</th>
                        <th className="py-3 font-medium">Thickness</th>
                      </tr>
                    </thead>
                    <tbody>
                      {p.variants.map((v, idx) => (
                        <tr
                          key={idx}
                          className="border-t border-[#E8E6DF]"
                          data-testid={`public-variant-row-${idx}`}
                        >
                          <td className="py-3 pr-4 font-semibold text-[#1A1A18]">{v.code}</td>
                          <td className="py-3 pr-4 text-[#1A1A18]">{v.finish || "—"}</td>
                          <td className="py-3 pr-4 text-[#1A1A18]">{v.length || "—"}</td>
                          <td className="py-3 pr-4 text-[#1A1A18]">{v.coverage || "—"}</td>
                          <td className="py-3 pr-4 text-[#1A1A18]">{v.weight || "—"}</td>
                          <td className="py-3 text-[#1A1A18]">{v.thickness || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {p.features?.length > 0 && (
              <section>
                <div className="hed-overline">Features</div>
                <h2 className="font-serif text-3xl md:text-4xl mt-2 mb-4">
                  Crafted to perform.
                </h2>
                <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2.5 text-[15px]">
                  {p.features.map((f, idx) => (
                    <li key={idx} className="flex items-start gap-2.5 min-w-0">
                      <span className="text-[#B5953B] mt-2 flex-shrink-0 w-1 h-1 rounded-full bg-[#B5953B]" />
                      <span className="flex-1 min-w-0 break-words">{f}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {(p.available_sizes?.length > 0 || p.available_colors?.length > 0) && (
              <section className="grid sm:grid-cols-2 gap-6 md:gap-8">
                {p.available_sizes?.length > 0 && (
                  <div>
                    <div className="hed-overline">Available Sizes</div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {p.available_sizes.map((s, i) => (
                        <span key={i} className="hed-chip text-[12px]">
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {p.available_colors?.length > 0 && (
                  <div>
                    <div className="hed-overline">Available Colors</div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {p.available_colors.map((c, i) => (
                        <span key={i} className="hed-chip text-[12px]">
                          {c}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </section>
            )}

            {p.technical_info && (
              <section>
                <div className="hed-overline">Technical Information</div>
                <h2 className="font-serif text-3xl md:text-4xl mt-2 mb-4">
                  Good to know.
                </h2>
                <BulletText
                  text={p.technical_info}
                  className="text-[15px] text-[#1A1A18]/90 leading-relaxed"
                />
              </section>
            )}

            {(p.installation_instructions ||
              approved.installation_recommendations?.length > 0) && (
              <section>
                <div className="hed-overline">Installation</div>
                <h2 className="font-serif text-3xl md:text-4xl mt-2 mb-4">
                  How it goes in.
                </h2>
                {p.installation_instructions && (
                  <BulletText
                    text={p.installation_instructions}
                    className="text-[15px] text-[#1A1A18]/90 leading-relaxed"
                  />
                )}
                {approved.installation_recommendations?.length > 0 && (
                  <ul className="mt-4 space-y-2 text-[15px]">
                    {approved.installation_recommendations.map((v, i) => (
                      <li key={i} className="flex items-start gap-2 min-w-0">
                        <Sparkles
                          size={12}
                          className="text-[#B5953B] mt-1.5 flex-shrink-0"
                          strokeWidth={2}
                        />
                        <span className="flex-1 min-w-0 break-words">{v}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )}

            {approved.recommended_applications?.length > 0 && (
              <section>
                <div className="hed-overline">Recommended Applications</div>
                <ul className="mt-4 grid sm:grid-cols-2 gap-x-8 gap-y-2 text-[15px]">
                  {approved.recommended_applications.map((v, i) => (
                    <li key={i} className="flex items-start gap-2 min-w-0">
                      <Sparkles
                        size={12}
                        className="text-[#B5953B] mt-1.5 flex-shrink-0"
                        strokeWidth={2}
                      />
                      <span className="flex-1 min-w-0 break-words">{v}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {approved.maintenance_instructions?.length > 0 && (
              <section>
                <div className="hed-overline">Maintenance</div>
                <ul className="mt-4 space-y-2 text-[15px]">
                  {approved.maintenance_instructions.map((v, i) => (
                    <li key={i} className="flex items-start gap-2.5 min-w-0">
                      <span className="text-[#B5953B] mt-2 flex-shrink-0 w-1 h-1 rounded-full bg-[#B5953B]" />
                      <span className="flex-1 min-w-0 break-words">{v}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {p.research_notes && (
              <section data-testid="public-research-section">
                <div className="flex items-center gap-2">
                  <Sparkles size={14} strokeWidth={2} className="text-[#B5953B]" />
                  <div className="hed-overline">Researched on the web</div>
                </div>
                <h2 className="font-serif text-3xl md:text-4xl mt-2 mb-4">
                  Beyond the brochure.
                </h2>
                <div className="text-[15px] text-[#1A1A18]/90 leading-relaxed space-y-4">
                  {p.research_notes
                    .split(/\n\s*\n/)
                    .filter((para) => para.trim())
                    .map((para, idx) => {
                      const lines = para.split("\n");
                      const head = lines[0]?.trim();
                      const isHead =
                        head &&
                        /^[A-Z][A-Z &']{3,40}:/.test(head);
                      if (isHead) {
                        const heading = head.replace(/:$/, "");
                        const rest = lines.slice(1).join(" ").trim();
                        return (
                          <div key={idx}>
                            <div className="text-[12px] font-semibold tracking-wider uppercase text-[#5E6033] mb-1">
                              {heading.replace(/:.*$/, "")}
                            </div>
                            <p className="text-[15px]">{rest || head.replace(/^[A-Z][A-Z &']{3,40}:\s*/, "")}</p>
                          </div>
                        );
                      }
                      return (
                        <p key={idx}>{para.replace(/\s+/g, " ").trim()}</p>
                      );
                    })}
                </div>
                {p.research_sources?.length > 0 && (
                  <div className="mt-5">
                    <div className="text-[11px] uppercase tracking-wider text-[#5C5C54] mb-2">
                      Sources
                    </div>
                    <ul className="space-y-1.5">
                      {p.research_sources.slice(0, 6).map((u, i) => (
                        <li key={i} className="text-xs break-words">
                          <a
                            href={u}
                            target="_blank"
                            rel="noreferrer"
                            className="text-[#5E6033] hover:text-[#B5953B] underline underline-offset-2"
                            data-testid={`public-research-source-${i}`}
                          >
                            {u}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </section>
            )}

            {p.reference_sheets?.filter((s) => s.include_public).length > 0 && (
              <section>
                <div className="hed-overline">Reference Sheets</div>
                <h2 className="font-serif text-3xl md:text-4xl mt-2 mb-4">
                  From the brochure.
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 md:gap-4">
                  {p.reference_sheets
                    .filter((s) => s.include_public)
                    .map((s, i) => (
                      <a
                        key={s.id}
                        href={fileUrl(s.storage_path)}
                        target="_blank"
                        rel="noreferrer"
                        className="block border border-[#E8E6DF] bg-white rounded-sm overflow-hidden hover:border-[#B5953B] transition-colors"
                        data-testid={`public-ref-sheet-${i}`}
                      >
                        <div className="aspect-[3/4] bg-[#F2F0EA] overflow-hidden">
                          <img
                            src={fileUrl(s.storage_path)}
                            alt={s.title || s.label}
                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                          />
                        </div>
                        <div className="p-2.5">
                          <div className="hed-overline" style={{ fontSize: 9 }}>
                            {s.label.replace("_", " ")}
                          </div>
                          <div className="text-xs font-semibold mt-0.5 truncate">{s.title}</div>
                        </div>
                      </a>
                    ))}
                </div>
              </section>
            )}

            {approved.faqs?.length > 0 && (
              <section>
                <div className="hed-overline">Frequently asked</div>
                <h2 className="font-serif text-3xl md:text-4xl mt-2 mb-5">
                  Good questions.
                </h2>
                <div className="divide-y divide-[#E8E6DF] border-t border-[#E8E6DF]">
                  {approved.faqs.map((f, idx) => (
                    <details key={idx} className="py-4 group" data-testid={`faq-item-${idx}`}>
                      <summary className="cursor-pointer flex items-center justify-between text-[15px] font-semibold list-none">
                        <span>{f.question}</span>
                        <span className="text-[#B5953B] group-open:rotate-45 transition-transform text-xl ml-3">
                          +
                        </span>
                      </summary>
                      <p className="text-[14px] text-[#5C5C54] mt-3 leading-relaxed">
                        {f.answer}
                      </p>
                    </details>
                  ))}
                </div>
              </section>
            )}
          </div>

          {/* Sidebar: docs */}
          <aside className="space-y-6">
            <div className="hed-card p-6">
              <div className="hed-overline">Documents</div>
              {p.files?.length > 0 ? (
                <div className="mt-3 space-y-2">
                  {p.files.map((f) => (
                    <a
                      key={f.id}
                      href={fileUrl(f.storage_path)}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center justify-between text-sm border border-[#E8E6DF] hover:bg-[#F2F0EA] px-3 py-2 rounded-sm transition-colors"
                      data-testid={`download-doc-${f.id}`}
                    >
                      <span className="truncate flex items-center gap-2">
                        <Download size={14} strokeWidth={2} className="text-[#5E6033]" />
                        {f.original_filename}
                      </span>
                    </a>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-[#5C5C54] mt-3">No downloadable documents.</p>
              )}
            </div>

            <div className="hed-card p-6 bg-[#F2F0EA] border-[#E8E6DF]">
              <div className="hed-overline">Heda AI assistant</div>
              <p className="font-serif text-2xl mt-2 leading-snug">Got a question?</p>
              <p className="text-xs text-[#5C5C54] mt-2">
                Tap the gold &ldquo;Ask Heda AI&rdquo; button to chat with the assistant trained on this product.
              </p>
            </div>
          </aside>
        </div>
      </article>

      <ChatWidget productCode={p.code} productName={p.name} />
    </PublicLayout>
  );
}

function KV({ label, value }) {
  return (
    <div>
      <div className="hed-overline" style={{ fontSize: 10 }}>
        {label}
      </div>
      <div className="text-sm font-semibold mt-1">{value || "—"}</div>
    </div>
  );
}

function unitLabel(unit) {
  const map = {
    piece: "piece",
    box: "box",
    sqft: "sq ft",
    sheet: "sheet",
    running_ft: "running ft",
  };
  return map[unit] || unit || "unit";
}
